import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

// Material UI
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { alpha, useTheme } from "@mui/material/styles";

// Icons
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import BuildIcon from "@mui/icons-material/Build";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";

// Components
import {
  AnimatedModal,
  ModalContent,
  ModalHeader,
} from "../../modals/AnimatedModal";
import RemoveModal from "../../modals/removeModal";
import CreateOrEditAgentModal from "./CreateOrEditAgentModal";

// Services & Utils
import AIAgentService from "../../../api/agent";
import { dispatchSnackBarMessage } from "../../../utils/snackbar";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { y: 10, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
};

const buttonVariants = {
  hover: { scale: 1.05 },
  tap: { scale: 0.95 },
};

// Agent row component
function AgentRow({ agent, onEdit, onDelete, onToggleActive }) {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);
  const profile = agent.profile;
  const isActive = profile?.is_active ?? true;
  const toolCount = profile?.allowed_actions?.length || 0;

  return (
    <motion.div
      variants={itemVariants}
      initial="hidden"
      animate="visible"
      layout
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
    >
      <Paper
        elevation={hovered ? 2 : 0}
        sx={{
          p: 2,
          mb: 1.5,
          borderRadius: 2,
          backgroundColor: hovered
            ? alpha(theme.palette.background.paper, 0.9)
            : theme.palette.grey[50],
          border: `1px solid ${
            hovered
              ? alpha(theme.palette.primary.main, 0.1)
              : alpha(theme.palette.grey[300], 0.5)
          }`,
          borderLeft: `4px solid ${
            isActive ? theme.palette.success.main : theme.palette.grey[400]
          }`,
          transition: "all 0.3s ease",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ flex: 1 }}
          >
            <Avatar
              sx={{
                bgcolor: isActive
                  ? alpha(theme.palette.primary.main, 0.1)
                  : alpha(theme.palette.grey[500], 0.1),
                color: isActive
                  ? theme.palette.primary.main
                  : theme.palette.grey[500],
                width: 40,
                height: 40,
              }}
            >
              <SmartToyIcon fontSize="small" />
            </Avatar>

            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={500} lineHeight={1.2}>
                {agent.first_name} {agent.last_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                @{agent.username}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              icon={<BuildIcon sx={{ fontSize: 14 }} />}
              label={toolCount > 0 ? `${toolCount} ferramentas` : "Todas"}
              size="small"
              variant="outlined"
              sx={{ height: 24, fontSize: "0.75rem" }}
            />

            <Chip
              icon={<PowerSettingsNewIcon sx={{ fontSize: 14 }} />}
              label={isActive ? "Ativo" : "Inativo"}
              size="small"
              color={isActive ? "success" : "default"}
              variant={isActive ? "filled" : "outlined"}
              sx={{ height: 24, fontSize: "0.75rem" }}
            />

            <Tooltip title="Editar agente">
              <motion.div
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <IconButton
                  size="small"
                  onClick={() => onEdit(agent)}
                  sx={{
                    color: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.2),
                    },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </motion.div>
            </Tooltip>

            <Tooltip title="Remover agente">
              <motion.div
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <IconButton
                  size="small"
                  onClick={() => onDelete(agent)}
                  sx={{
                    color: theme.palette.error.main,
                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.error.main, 0.2),
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </motion.div>
            </Tooltip>
          </Stack>
        </Stack>

        {profile?.instructions && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 1,
              pl: 7,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {profile.instructions}
          </Typography>
        )}
      </Paper>
    </motion.div>
  );
}

export default function AgentsModal({ show, closeModal }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);

  const [agents, setAgents] = useState([]);
  const [availableTools, setAvailableTools] = useState([]);
  const [editingAgent, setEditingAgent] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (show) {
      Promise.all([loadAgents(), loadTools()]);
    }
  }, [show]);

  const loadAgents = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await AIAgentService.getAIAgents();
      setAgents(data);
    } catch (e) {
      const message = e.response?.data || "Erro ao carregar agentes IA.";
      setErrorMessage(message);
      setShowError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTools = useCallback(async () => {
    try {
      const { data } = await AIAgentService.getAIAgentTools();
      setAvailableTools(data);
    } catch (e) {
      console.error("Failed to load tools", e);
    }
  }, []);

  const handleCreateModal = useCallback(() => {
    setShowCreateModal((prev) => !prev);
    setEditingAgent(null);
  }, []);

  const handleCreateAgent = useCallback(
    async (agentObj) => {
      try {
        setLoading(true);
        handleCreateModal();
        await AIAgentService.createAIAgent(agentObj);

        dispatchSnackBarMessage(
          `agent-created-${new Date()}`,
          "Agente IA criado com sucesso",
          "success"
        );

        await loadAgents();
      } catch (e) {
        const message =
          e.response?.data?.message ||
          e.response?.data?.username?.[0] ||
          "Erro ao criar agente IA";
        dispatchSnackBarMessage(`agent-error-${new Date()}`, message, "error");
      } finally {
        setLoading(false);
      }
    },
    [handleCreateModal, loadAgents]
  );

  const handleEditAgent = useCallback((agent) => {
    setEditingAgent(agent);
    setShowCreateModal(true);
  }, []);

  const handleUpdateAgent = useCallback(
    async (agentObj) => {
      try {
        setLoading(true);
        await AIAgentService.updateAIAgent(editingAgent.id, agentObj);

        setEditingAgent(null);
        dispatchSnackBarMessage(
          `agent-updated-${new Date()}`,
          "Agente IA atualizado com sucesso",
          "success"
        );
      } catch (e) {
        const message =
          e.response?.data?.message || "Erro ao atualizar agente IA";
        dispatchSnackBarMessage(`agent-error-${new Date()}`, message, "error");
      } finally {
        setLoading(false);
        handleCreateModal();
        await loadAgents();
      }
    },
    [editingAgent, handleCreateModal, loadAgents]
  );

  const handleDeleteModalToggle = useCallback((agent = null) => {
    setAgentToDelete(agent);
    setShowDeleteModal((prev) => !prev);
  }, []);

  const handleDeleteAgent = useCallback(async () => {
    try {
      await AIAgentService.deleteAIAgent(agentToDelete.id);
      handleDeleteModalToggle();
      await loadAgents();

      dispatchSnackBarMessage(
        `agent-deleted-${new Date()}`,
        "Agente IA removido com sucesso",
        "success"
      );
    } catch (e) {
      dispatchSnackBarMessage(
        `agent-error-${new Date()}`,
        "Erro ao remover agente IA",
        "error"
      );
    }
  }, [agentToDelete, handleDeleteModalToggle, loadAgents]);

  const filteredAgents = useMemo(() => {
    if (!searchTerm) return agents;
    const lower = searchTerm.toLowerCase();
    return agents.filter(
      (a) =>
        `${a.first_name} ${a.last_name}`.toLowerCase().includes(lower) ||
        a.username.toLowerCase().includes(lower)
    );
  }, [agents, searchTerm]);

  return (
    <AnimatedModal
      isOpen={show}
      onClose={closeModal}
      width="80%"
      maxWidth="900px"
    >
      <ModalHeader
        title="Agentes IA"
        icon={SmartToyIcon}
        onClose={closeModal}
      />
      <ModalContent>
        <Box sx={{ px: 3, pt: 2 }}>
          <AnimatePresence>
            {showError && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <Alert
                  severity="error"
                  onClose={() => setShowError(false)}
                  sx={{ mb: 3, borderRadius: 2 }}
                >
                  {typeof errorMessage === "string"
                    ? errorMessage
                    : "Erro ao carregar agentes"}
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Header actions */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <motion.div
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateModal}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 500 }}
              >
                Novo Agente IA
              </Button>
            </motion.div>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                component="input"
                placeholder="Buscar agente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderRadius: 2,
                  py: 1,
                  px: 2,
                  width: 200,
                  outline: "none",
                  "&:focus": {
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 0 0 2px ${alpha(
                      theme.palette.primary.main,
                      0.2
                    )}`,
                  },
                  fontSize: "0.875rem",
                }}
              />
              <SearchIcon
                sx={{
                  ml: -3,
                  color: theme.palette.text.secondary,
                  pointerEvents: "none",
                }}
              />
            </Box>
          </Box>

          {/* Agents list header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
            }}
          >
            <SmartToyIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
            <Typography variant="h6" fontWeight={500}>
              Agentes Cadastrados
            </Typography>
            <Chip
              label={filteredAgents.length}
              size="small"
              color="primary"
              sx={{ ml: 1, height: 20, minWidth: 20 }}
            />
          </Box>

          {/* Agents list */}
          <Box
            sx={{
              backgroundColor: alpha(theme.palette.background.paper, 0.4),
              borderRadius: 3,
              p: 2,
              boxShadow: `inset 0 0 20px ${alpha(
                theme.palette.common.black,
                0.03
              )}`,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              minHeight: 300,
            }}
          >
            {loading ? (
              <Box sx={{ pt: 2 }}>
                {[1, 2, 3].map((item) => (
                  <Skeleton
                    key={item}
                    variant="rectangular"
                    height={70}
                    sx={{ borderRadius: 2, mb: 2 }}
                  />
                ))}
              </Box>
            ) : (
              <AnimatePresence>
                {filteredAgents.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        py: 8,
                        px: 2,
                      }}
                    >
                      <SmartToyIcon
                        sx={{
                          fontSize: 64,
                          color: alpha(theme.palette.text.secondary, 0.3),
                          mb: 2,
                        }}
                      />
                      <Typography
                        variant="h6"
                        color="textSecondary"
                        align="center"
                        gutterBottom
                      >
                        {searchTerm
                          ? "Nenhum agente encontrado"
                          : "Nenhum agente IA cadastrado"}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        align="center"
                        sx={{ mb: 3, maxWidth: 400 }}
                      >
                        {searchTerm
                          ? `Nenhum resultado para "${searchTerm}"`
                          : "Cadastre agentes IA para automatizar sua operação"}
                      </Typography>
                      {!searchTerm && (
                        <motion.div
                          variants={buttonVariants}
                          whileHover="hover"
                          whileTap="tap"
                        >
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleCreateModal}
                            sx={{ borderRadius: 2 }}
                          >
                            Adicionar Agente IA
                          </Button>
                        </motion.div>
                      )}
                    </Box>
                  </motion.div>
                ) : (
                  <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    {filteredAgents.map((agent) => (
                      <AgentRow
                        key={agent.id}
                        agent={agent}
                        onEdit={handleEditAgent}
                        onDelete={handleDeleteModalToggle}
                      />
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </Box>
        </Box>

        <RemoveModal
          title="Remover Agente IA"
          message={`Tem certeza que deseja desativar ${
            agentToDelete?.first_name ?? ""
          } ${agentToDelete?.last_name ?? ""}?`}
          show={showDeleteModal}
          toggleModal={handleDeleteModalToggle}
          onRemove={handleDeleteAgent}
        />

        {showCreateModal && (
          <CreateOrEditAgentModal
            show={showCreateModal}
            closeModal={handleCreateModal}
            createAgent={handleCreateAgent}
            editAgent={handleUpdateAgent}
            agent={editingAgent}
            availableTools={availableTools}
          />
        )}
      </ModalContent>
    </AnimatedModal>
  );
}
